import styled, { css } from 'styled-components';
import { white, primary, neutral, success, error } from '../../style/colors';

const center = css`display: flex; align-items: center; justify-content: center;`;

export const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9998;
  opacity: ${p => p.isOpen ? 1 : 0}; visibility: ${p => p.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s, visibility 0.3s;
`;

export const Panel = styled.div`
  position: fixed; top: 0; right: 0; bottom: 0; width: min(90%, 1200px);
  background: ${white}; box-shadow: -4px 0 20px rgba(0,0,0,0.15); z-index: 9999;
  transform: translateX(${p => p.isOpen ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex; flex-direction: column;
  @media (max-width: 768px) { width: 100%; }
`;

export const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 20px; background: ${primary[500]}; color: ${white}; flex-shrink: 0;
`;

export const HeaderTitle = styled.h2`
  margin: 0; font-size: 16px; font-weight: 600; ${center}; gap: 8px;
  i { font-size: 18px; }
`;

export const HeaderActions = styled.div`${center}; gap: 12px;`;

export const ClearButton = styled.button`
  ${center}; gap: 6px; padding: 6px 12px; background: rgba(255,255,255,0.2);
  color: ${white}; border: 1px solid ${white}; border-radius: 4px;
  cursor: pointer; font-size: 13px; font-weight: 500;
  &:hover { background: rgba(255,255,255,0.3); }
`;

export const CloseButton = styled.button`
  ${center}; padding: 0; background: transparent; color: ${white};
  border: none; cursor: pointer; font-size: 24px;
  &:hover { opacity: 0.7; }
`;

export const Content = styled.div`
  flex: 1; overflow: auto; padding: 30px; background: ${neutral[50]};
  @media (max-width: 768px) { padding: 20px 15px; }
`;

export const EmptyState = styled.div`
  ${center}; flex-direction: column; padding: 80px 20px; color: ${neutral[500]}; text-align: center;
  i { font-size: 80px; margin-bottom: 20px; opacity: 0.3; }
  h3 { margin: 0 0 10px; font-size: 24px; font-weight: 600; color: ${neutral[900]}; }
  p { margin: 0; font-size: 16px; line-height: 1.5; }
`;

export const ComparisonGrid = styled.div`
  display: grid; grid-template-columns: repeat(${p => Math.min(p.columns, 4)}, 1fr); gap: 20px;
  @media (max-width: 1024px) { grid-template-columns: repeat(${p => Math.min(p.columns, 2)}, 1fr); }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const TyreCard = styled.div`
  background: ${white}; border-radius: 16px; border: 1px solid ${neutral[200]};
  border-top: 4px solid ${p => p.brandColor || primary[500]};
  box-shadow: 0 4px 20px rgba(0,0,0,0.06); display: flex; flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-4px); box-shadow: 0 8px 25px rgba(0,0,0,0.1); }
`;

export const TyreHeader = styled.div`
  padding: 24px 20px; border-bottom: 1px solid ${neutral[100]}; position: relative;
`;

export const RemoveButton = styled.button`
  ${center}; position: absolute; top: 10px; right: 10px; width: 32px; height: 32px;
  background: ${neutral[100]}; color: ${neutral[500]}; border: 1px solid ${neutral[200]};
  border-radius: 6px; cursor: pointer; font-size: 18px; transition: all 0.2s;
  &:hover { background: ${error.base}; color: ${white}; border-color: ${error.base}; }
`;

export const BrandLogo = styled.img`
  width: 120px; height: auto; margin: 0 auto 15px; display: block; object-fit: contain;
`;

export const TyreName = styled.h3`
  margin: 0 0 12px; font-size: 18px; font-weight: 700; color: ${neutral[900]}; text-align: center;
`;

export const TyreDesign = styled.div`
  font-size: 14px; color: ${neutral[500]}; text-align: center; margin-bottom: 8px; font-weight: 500;
`;

export const Dimensions = styled.div`
  font-size: 16px; font-weight: 600; color: ${primary[500]}; text-align: center; margin-bottom: 12px;
`;

export const SeasonBadge = styled.div`
  ${center}; gap: 6px; padding: 6px 12px; background: ${neutral[100]};
  border-radius: 20px; font-size: 12px; font-weight: 600; color: ${neutral[500]};
`;

export const TyreBody = styled.div`padding: 20px; display: flex; flex-direction: column; flex: 1;`;

export const PriceDisplay = styled.div`
  padding: 15px; background: ${p => p.isBest ? success.light : neutral[100]};
  border: 2px solid ${p => p.isBest ? success.base : primary[500]};
  border-radius: 8px; text-align: center; margin-bottom: 15px; position: relative;
`;

export const BestPriceBadge = styled.span`
  position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
  background: ${success.base}; color: ${white}; font-size: 10px; padding: 2px 8px;
  border-radius: 10px; font-weight: 600; text-transform: uppercase;
`;

export const Price = styled.div`font-size: 28px; font-weight: 700; color: ${neutral[900]}; margin-bottom: 4px;`;

// Use same-hue color on colored backgrounds (RefactoringUI principle)
export const PriceLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${p => p.isBest ? success.dark : neutral[500]};
`;

export const StockBadge = styled.div`
  ${center}; justify-content: space-between; padding: 8px 16px;
  background: ${p => p.inStock ? success.light : error.light};
  border: 1px solid ${p => p.inStock ? success.base : error.base}; border-radius: 999px;
  font-size: 13px; font-weight: 600; color: ${p => p.inStock ? success.dark : error.dark}; margin-bottom: 15px;
`;

export const InfoSection = styled.div`margin-top: 15px;`;
export const InfoGrid = styled.div`display: grid; gap: 10px;`;
export const InfoRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px; background: ${neutral[100]}; border-radius: 6px; font-size: 14px;
`;
export const InfoLabel = styled.span`color: ${neutral[500]}; font-weight: 500;`;
export const InfoValue = styled.span`color: ${neutral[900]}; font-weight: 600; ${center}; gap: 6px;`;

export const EULabel = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;`;
export const EULabelItem = styled.div`
  ${center}; flex-direction: column; gap: 6px; padding: 12px 8px;
  background: ${p => p.isBest ? success.light : neutral[100]};
  border: 1px solid ${p => p.isBest ? success.base : neutral[200]};
  border-radius: 8px; min-height: 80px;
  i { font-size: 18px; color: ${p => p.isBest ? success.dark : neutral[500]}; }
`;
export const EULabelValue = styled.div`font-size: 20px; font-weight: 700; color: ${neutral[900]};`;

// Use same-hue color on colored backgrounds (RefactoringUI principle)
export const EULabelLabel = styled.div`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${p => p.isBest ? success.dark : neutral[500]};
`;

export const LinksRow = styled.div`display: flex; gap: 8px; margin-top: auto; padding-top: 15px;`;
export const ExternalLink = styled.a`
  ${center}; gap: 6px; flex: 1; padding: 8px 12px; background: ${neutral[100]};
  border: 1px solid ${neutral[200]}; border-radius: 6px; color: ${neutral[500]};
  text-decoration: none; font-size: 12px; font-weight: 500; transition: all 0.2s;
  &:hover { background: ${primary[500]}; color: ${white}; border-color: ${primary[500]}; }
`;
